import { useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";

export function useFoldingAnimation({
  panelId,
  index,
  panels,
  direction,
  width,
  isOpenTrigger,
  groupRef,
}) {
  const [angle, setAngle] = useState(0);
  const [slide, setSlide] = useState(0);
  const didInitClose = useRef(false);
  const didInitOpen = useRef(false);
  const speed = 0.06;

  const pivotX =
    direction === 1 || direction === 2
      ? width / 2 - 0.05
      : direction === 3 || direction === 4
        ? -width / 2 + 0.05
        : 0;

  // Her panelId + direction için bağımsız state
  window.__panelStates = window.__panelStates || {};
  window.__panelStates[panelId] = window.__panelStates[panelId] || {};
  const state = window.__panelStates[panelId];
  const dirKey = `dir${direction}`;

  if (!state[dirKey]) {
    let sameDirIndexes = panels
      .map((p, i) => ({ direction: p.direction, i }))
      .filter((p) => p.direction === direction)
      .map((p) => p.i);

    // Eğer direction sağa doğruysa sıralamayı ters çevir
    if (direction === 3 || direction === 4) {
      sameDirIndexes = sameDirIndexes.reverse();
    }

    state[dirKey] = {
      activeIndex: sameDirIndexes[0],
      closingIndex: sameDirIndexes[sameDirIndexes.length - 1],
      dirIndexes: sameDirIndexes,
    };
  }

  const panelState = state[dirKey];

  useFrame(() => {
    if (!groupRef.current) return;

    // Eğer bu direction grubunda hiç açılır cam (dir1/dir4) yoksa ve sadece kayar camlarsa animasyon çalışmasın
    const dirIndexes = state[`dir${direction}`]?.dirIndexes || [];
    const allSliding = dirIndexes.every((i) => {
      const d = panels[i]?.direction;
      return d === 2 || d === 3;
    });
    const hasNoOpeningPanels =
      !panels.some((p) => p.direction === 1 || p.direction === 4);

    if ((direction === 2 || direction === 3) && allSliding && hasNoOpeningPanels) {
      return;
    }

    const isOpening = isOpenTrigger;
    const isClosing = !isOpenTrigger;
    const currentIndex = isOpening ? panelState.activeIndex : panelState.closingIndex;
    const isMyTurn = index === currentIndex;

    if (isClosing && !didInitClose.current) {
      panelState.closingIndex =
        panelState.dirIndexes[panelState.dirIndexes.length - 1];
      didInitClose.current = true;
    }

    if (isOpening && !didInitOpen.current) {
      panelState.activeIndex = panelState.dirIndexes[0];
      didInitOpen.current = true;
    }

    if (!isOpening && didInitOpen.current) didInitOpen.current = false;
    if (!isClosing && didInitClose.current) didInitClose.current = false;

    if (!isMyTurn) return;

    // 🔒 Kapatırken: önce kayar camlar kapanmalı, sonra açılır cam
    if (isClosing && (direction === 1 || direction === 4)) {
      const blockerDir = direction === 1 ? 2 : 3;
      const blockerState = state[`dir${blockerDir}`];
      if (blockerState && blockerState.closingIndex != -1) {
        return; // önce kayar camlar kapansın
      }
    }

    // AÇILIR CAMLAR
    if (direction === 1 || direction === 4) {
      const target = isOpening
        ? direction === 1
          ? -Math.PI / 2
          : Math.PI / 2
        : 0;

      const diff = Math.abs(angle - target);

      if (diff > 0.001) {
        const newAngle = angle + (target - angle) * 0.1;
        setAngle(newAngle);
        groupRef.current.rotation.y = newAngle;
      } else {
        setAngle(target);
        groupRef.current.rotation.y = target;

        const dirList = panelState.dirIndexes;
        const currentIdx = dirList.indexOf(index);
        const nextIdx = isOpening ? currentIdx + 1 : currentIdx - 1;
        const nextPanelIndex = dirList[nextIdx];

        if (isOpening) {
          panelState.activeIndex = nextPanelIndex ?? -1;
        } else {
          panelState.closingIndex = nextPanelIndex ?? -1;
        }
      }
    }

    // KAYAR CAMLAR
    if (direction === 2 || direction === 3) {


      if (direction === 3) {
        const hasDir4 = panels.some((p) => p.direction === 4);
        if (!hasDir4) return;
      } else if (direction === 2) {
        const hasDir1 = panels.some((p) => p.direction === 1);
        if (!hasDir1) return;
      }
      
      const group = panelState.dirIndexes;
      const myIndexInGroup = group.indexOf(index);
      const slideCount = myIndexInGroup;
      const dirMultiplier = direction === 2 ? 1 : -1;
      const angleTarget = direction === 2 ? -Math.PI / 2 : Math.PI / 2;
      const slideDistance = dirMultiplier * ((width - 0.08) * (slideCount + 1));
      const slideDone = Math.abs(slide - slideDistance) <= 0.001;
      const rotateDone = Math.abs(angle - angleTarget) <= 0.001;

      if (isOpening) {
        if (!slideDone) {
          const newSlide = slide + (slideDistance - slide) * speed;
          setSlide(newSlide);
        } else if (!rotateDone) {
          const newAngle = angle + (angleTarget - angle) * speed;
          setAngle(newAngle);
        } else {
          const nextIndex = group[myIndexInGroup + 1];
          panelState.activeIndex = nextIndex ?? -1;
        }
      } else {
        if (Math.abs(angle) > 0.001) {
          const newAngle = angle + (0 - angle) * speed;
          setAngle(newAngle);
        } else if (Math.abs(slide) > 0.001) {
          const newSlide = slide + (0 - slide) * speed;
          setSlide(newSlide);
        } else {
          const nextIndex = group[myIndexInGroup - 1];
          panelState.closingIndex = nextIndex ?? -1;
        }
      }

      groupRef.current.position.x = slide + pivotX;
      groupRef.current.rotation.y = angle;
    }
  });

  return { angle, slide, pivotX };
}
